import {FormLabel} from '@mui/material';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Grid from '@mui/material/Grid';
import _ from 'lodash';
import {useStylesECDS} from './ecdsStyle';
import clsx from 'clsx';
import AgyleTextField from '../../../../components/ui/fields/agyleTextField';
import {cleanObject} from '../../../../util/patientContextUtil';

const PersonData = (props) => {
    const {t} = useTranslation();
    const classes = useStylesECDS();

    const {isSmall} = props;

    // Patient Name and Address Dataset
    const [patientName, setPatientName] = useState({
        PersonFullName: '',
        PersonRequestedName: '',
        PersonTitle: '',
        PersonGivenName: '',
        PersonFamilyName: '',
        PersonNameSuffix: '',
        PersonInitials: '',
    });
    const [patientAddress, setPatientAddress] = useState({
        UnstructuredAddress: '',
        StructuredAddress: '',
    });

    // For ReadOnly Data
    const disableData = useRef({});

    const [disabledFullName, setDisabledFullName] = useState(false);
    const [disabledRequestedName, setDisabledRequestedName] = useState(false);
    const [disabledStructuredName, setDisableStructuredName] = useState(false);

    // Initial Data
    useEffect(() => {
        let name = {};
        let address = {};

        if (props.patient?.studies?.EmergencyCare?.PersonGroupPatient?.PatientIdentity?.UnverifiedIdentity_Structure) {
            const clone = _.cloneDeep(props.patient?.studies?.EmergencyCare?.PersonGroupPatient?.PatientIdentity?.UnverifiedIdentity_Structure?.DataElementStructure);
            name = {
                ...clone?.PatientName?.PersonName_Structured,
                PersonFullName: clone?.PatientName?.PersonFullName,
                PersonRequestedName: clone?.PatientName?.PersonRequestedName,
            };
            address = {...clone?.PatientName?.PatientUsualAddress};
        }

        if (props.patient?.patient) {
            name = {
                ...name,
                PersonTitle: props.patient?.patient?.title || '',
                PersonGivenName: props.patient?.patient?.firstName || '',
                PersonFamilyName: props.patient?.patient?.familyName || '',
            };
            address = {
                ...address,
                StructuredAddress: props.patient?.patient?.addressLines || '',
            };
            setPatientName((prevState) => ({...prevState, ...name}));
            setPatientAddress((prevState) => ({...prevState, ...address}));

            disableData.current = {...cleanObject(name), ...cleanObject(address)};
        }
    }, []);

    useEffect(() => {
        const data = {
            PersonFullName: patientName.PersonFullName,
            PersonName_Structured: {
                PersonTitle: patientName.PersonTitle,
                PersonGivenName: patientName.PersonGivenName,
                PersonFamilyName: patientName.PersonFamilyName,
                PersonNameSuffix: patientName.PersonNameSuffix,
                PersonInitials: patientName.PersonInitials,
            },
            PersonRequestedName: patientName.PersonRequestedName,
        };
        validateName();
        props.setName(data);
    }, [patientName]);

    useEffect(() => {
        const data = {
            UnstructuredAddress: patientAddress.UnstructuredAddress,
            StructuredAddress: patientAddress.StructuredAddress,
        };
        props.setAddress(data);
    }, [patientAddress]);

    const dateHandler = (event) => {
        setPatientName((prevState) => ({...prevState, [event.target.name]: event.target.value}));
    };

    const validateName = () => {
        const structuredName = patientName.PersonGivenName !== '' || patientName.PersonFamilyName !== '' || patientName.PersonNameSuffix !== '' || patientName.PersonInitials !== '';

        setDisabledFullName(patientName.PersonRequestedName || structuredName);
        setDisabledRequestedName(patientName.PersonFullName || structuredName);
        setDisableStructuredName(!!(patientName.PersonRequestedName || patientName.PersonFullName));
    };

    return (
        <>
            <div style={{marginTop: 15}}>
                <FormLabel className={classes.label}>
                    {t('Patient Name')}
                </FormLabel>
            </div>

            <div style={{marginBottom: 10}}>
                <FormLabel className={clsx(classes.label, 'small')}>
                    {t('Choose only one option group')}
                </FormLabel>
            </div>

            <Grid container spacing={2} direction="row">
                <Grid item xs={12}>
                    <AgyleTextField
                        name="PersonFullName"
                        label={t('Patient Name')}
                        onChange={dateHandler}
                        value={patientName.PersonFullName}
                        disabled={disabledFullName}
                        fullWidth
                        readOnly={!!disableData?.current.PersonFullName}
                        maxLength={70}
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <AgyleTextField
                        name="PersonRequestedName"
                        label={t('Patient Requested Name')}
                        value={patientName.PersonRequestedName}
                        onChange={dateHandler}
                        disabled={disabledRequestedName}
                        fullWidth
                        readOnly={!!disableData?.current.PersonRequestedName}
                        maxLength={70}
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="PersonGivenName"
                        label={t('Person Given Name')}
                        onChange={dateHandler}
                        value={patientName.PersonGivenName}
                        disabled={disabledStructuredName}
                        fullWidth
                        readOnly={!!disableData?.current.PersonGivenName}
                        maxLength={70}
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 6}>
                    <AgyleTextField
                        name="PersonFamilyName"
                        label={t('Person Family Name')}
                        value={patientName.PersonFamilyName}
                        onChange={dateHandler}
                        disabled={disabledStructuredName}
                        fullWidth
                        readOnly={!!disableData?.current.PersonFamilyName}
                        maxLength={70}
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 4}>
                    <AgyleTextField
                        name="PersonTitle"
                        label={t('Person Title')}
                        value={patientName.PersonTitle}
                        onChange={dateHandler}
                        disabled={disabledStructuredName}
                        fullWidth
                        readOnly={!!disableData?.current.PersonTitle}
                        maxLength={70}
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 4}>
                    <AgyleTextField
                        name="PersonNameSuffix"
                        label={t('Person Name Suffix')}
                        value={patientName.PersonNameSuffix}
                        onChange={dateHandler}
                        disabled={disabledStructuredName}
                        fullWidth
                        readOnly={!!disableData?.current.PersonNameSuffix}
                        maxLength={20}
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={isSmall ? 12 : 4}>
                    <AgyleTextField
                        name="PersonInitials"
                        label={t('Person Initials')}
                        value={patientName.PersonInitials}
                        onChange={dateHandler}
                        disabled={disabledStructuredName}
                        fullWidth
                        readOnly={!!disableData?.current.PersonInitials}
                        maxLength={10}
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>
            </Grid>

            <div style={{marginTop: 15}}>
                <FormLabel className={classes.label}>
                    {t('Patient Address')}
                </FormLabel>
            </div>

            <div style={{marginBottom: 10}}>
                <FormLabel className={clsx(classes.label, 'small')}>
                    {t('Choose only one option group')}
                </FormLabel>
            </div>

            <Grid container spacing={2} direction="row">
                <Grid item xs={12}>
                    <AgyleTextField
                        name="UnstructuredAddress"
                        onChange={dateHandler}
                        label={t('Patient Usual Address (Unstructured)')}
                        value={patientAddress.UnstructuredAddress}
                        disabled={patientAddress.StructuredAddress !== ''}
                        fullWidth
                        readOnly={!!disableData?.current.UnstructuredAddress}
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>

                <Grid item xs={12}>
                    <AgyleTextField
                        name="StructuredAddress"
                        onChange={dateHandler}
                        label={t('Patient Usual Address (Structured)')}
                        multiline
                        rows={5}
                        value={patientAddress.StructuredAddress || ''}
                        disabled={patientAddress.UnstructuredAddress !== ''}
                        fullWidth
                        maxLength={175}
                        readOnly={!!disableData?.current.StructuredAddress}
                        debounce
                        noBorder={false}
                        withNewBackground
                        size="large"
                    />
                </Grid>
            </Grid>
        </>
    );
};

export default PersonData;
